import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

const pluginKey = new PluginKey('genderAgreementHighlight');

const GenderAgreementExtension = Extension.create({
    name: 'genderAgreementHighlight',

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: pluginKey,
                state: {
                    init(_, { doc }) {
                        return findGenderAgreements(doc);
                    },
                    apply(transaction, oldState) {
                        if (!transaction.docChanged) {
                            return oldState.map(transaction.mapping, transaction.doc);
                        }
                        return findGenderAgreements(transaction.doc);
                    }
                },
                props: {
                    decorations(state) {
                        return pluginKey.getState(state);
                    }
                }
            })
        ];
    }
});

function findGenderAgreements(doc) {
    const decorations = [];
    // Regex для поиска конструкций {муж|жен} или {муж|жен|сред:переменная}
    // Второй/третий варианты не должны захватывать часть после ":" — исключаем двоеточие
    const agreementRegex = /\{([^{}|]+)\|([^{}|:]+)(?:\|([^{}|:]+))?(?::([^{}]+))?\}/g;

    // console.log('GenderAgreementExtension: Scanning document...'); // Отладка

    doc.descendants((node, pos) => {
        if (node.isText && node.text) {
            let match;
            const text = node.text;
            agreementRegex.lastIndex = 0;

            // if (text.includes('{')) {
            //    console.log('GenderAgreementExtension: Found text with "{":', text);
            // }

            while ((match = agreementRegex.exec(text)) !== null) {
                // Не подсвечиваем конструкции внутри {{...}} (переменные шаблона).
                // Пример: {{debtor_case_act_type|lowerFirst}} содержит "{...|...}", но это не согласование.
                const startIdx = match.index;
                const endIdx = startIdx + match[0].length;
                if ((startIdx > 0 && text[startIdx - 1] === '{') || (endIdx < text.length && text[endIdx] === '}')) {
                  continue;
                }

                const from = pos + match.index;
                const to = from + match[0].length;
                const hasNeutral = !!match[3];
                const maleLen = match[1].length;
                const femaleLen = match[2].length;
                const neutralLen = hasNeutral ? match[3].length : 0;

                // Градиент по словам, один контур
                const totalLen = match[0].length;
                const maleStart = 1; // после "{"
                const maleEnd = maleStart + maleLen;
                const femaleStart = maleEnd + 1; // после |
                const femaleEnd = femaleStart + femaleLen;
                const neutralStart = hasNeutral ? femaleEnd + 1 : null; // после второго |
                const neutralEnd = hasNeutral ? neutralStart + neutralLen : null;

                const pct = (pos) => Math.max(0, Math.min(100, (pos / totalLen) * 100));

                const stops = [
                  `transparent 0% ${pct(maleStart)}%`,
                  `rgba(33,150,243,0.22) ${pct(maleStart)}% ${pct(maleEnd)}%`,
                  `transparent ${pct(maleEnd)}% ${pct(femaleStart)}%`,
                  `rgba(233,30,99,0.22) ${pct(femaleStart)}% ${pct(femaleEnd)}%`,
                ];

                if (hasNeutral && neutralStart !== null && neutralEnd !== null) {
                  stops.push(`transparent ${pct(femaleEnd)}% ${pct(neutralStart)}%`);
                  stops.push(`rgba(0,150,136,0.22) ${pct(neutralStart)}% ${pct(neutralEnd)}%`);
                  stops.push(`transparent ${pct(neutralEnd)}% 100%`);
                } else {
                  stops.push(`transparent ${pct(femaleEnd)}% 100%`);
                }

                const gradient = `linear-gradient(90deg, ${stops.join(',')})`;

                decorations.push(
                  Decoration.inline(from, to, {
                    class: 'template-variable-highlight gender-agreement-highlight',
                    title: `Согласование по роду: ${match[0]}`,
                    'data-gender-agreement': 'true',
                    'data-gender-count': hasNeutral ? '3' : '2',
                    style: `background:${gradient} !important;background-size:100% 100% !important;`
                  })
                );
            }
        }
    });

    return DecorationSet.create(doc, decorations);
}

export default GenderAgreementExtension;
